import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { forceHardRefresh } from '../lib/pwaUpdate';
import {
  DISPLAY_CURRENCIES,
  ORDER_LANG_OPTIONS,
  PORTAL_URL,
  SUPPORT_WHATSAPP_URL,
  feeOptionsForCurrency,
  formatFeeOption,
  orderLangLabel,
  typeLabel,
  vendorCurrency,
} from '../constants';

export default function SettingsPage() {
  const { vendor, logout, refreshShop } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [currencyBusy, setCurrencyBusy] = useState(false);
  const [feeBusy, setFeeBusy] = useState(false);
  const [minOrderBusy, setMinOrderBusy] = useState(false);
  const [orderLangBusy, setOrderLangBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('AED');
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [minimumOrder, setMinimumOrder] = useState('');
  const [orderLang, setOrderLang] = useState('en');
  const [qrDataUrl, setQrDataUrl] = useState('');

  const walkInUrl = useMemo(() => {
    const id = vendor?.id;
    if (!id) return 'https://user.gosnel.com/';
    return `https://user.gosnel.com/?lp=${id}`;
  }, [vendor?.id]);

  const feeOptions = useMemo(
    () => feeOptionsForCurrency(displayCurrency, vendor?.delivery_fee_options),
    [displayCurrency, vendor?.delivery_fee_options],
  );

  useEffect(() => {
    setDisplayCurrency(vendorCurrency(vendor));
    const lang = String(vendor?.neighbour_order_lang || 'en')
      .trim()
      .toLowerCase();
    setOrderLang(ORDER_LANG_OPTIONS.some((o) => o.value === lang) ? lang : 'en');
    const rawFee = Number(vendor?.delivery_fee_amount);
    const opts = feeOptionsForCurrency(
      vendorCurrency(vendor),
      vendor?.delivery_fee_options,
    );
    const matched = opts.find((o) => Math.abs(o - (Number.isFinite(rawFee) ? rawFee : 0)) < 0.001);
    setDeliveryFee(matched != null ? matched : 0);
    const rawMin = Number(vendor?.minimum_order_amount);
    setMinimumOrder(Number.isFinite(rawMin) && rawMin > 0 ? String(rawMin) : '');
  }, [vendor]);

  useEffect(() => {
    // When currency draft changes, clamp fee to a valid option for that currency.
    const matched = feeOptions.find((o) => Math.abs(o - deliveryFee) < 0.001);
    if (matched == null && feeOptions.length) {
      setDeliveryFee(feeOptions[0]);
    }
  }, [feeOptions, deliveryFee]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = await QRCode.toDataURL(walkInUrl, {
          width: 512,
          margin: 2,
          color: { dark: '#0f766e', light: '#ffffff' },
        });
        if (!cancelled) setQrDataUrl(url);
      } catch {
        if (!cancelled) setQrDataUrl('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [walkInUrl]);

  const changePassword = async () => {
    setError('');
    setSuccess('');
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/vendor/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess('Password updated. Use the new password next login.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError(
          'Password change is not enabled on this API yet. Ask GoSnel HQ (Share login) to reset it.'
        );
      } else {
        setError(formatApiError(err, 'Could not change password'));
      }
    } finally {
      setBusy(false);
    }
  };

  const saveCurrency = async () => {
    setError('');
    setSuccess('');
    setCurrencyBusy(true);
    const payload = {
      display_currency: displayCurrency,
      delivery_fee_amount: deliveryFee,
    };
    try {
      try {
        await api.patch('/vendor/auth/local-partner-settings', payload);
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          await api.put('/vendor/auth/settings', payload);
        } else {
          throw err;
        }
      }
      await refreshShop();
      setSuccess(`Display currency set to ${displayCurrency}.`);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(
          'Currency setting is not available on the API yet. Your selection was kept locally for this session.'
        );
        try {
          const raw = localStorage.getItem('lp_vendor');
          if (raw) {
            const v = JSON.parse(raw);
            v.display_currency = displayCurrency;
            v.delivery_fee_amount = deliveryFee;
            localStorage.setItem('lp_vendor', JSON.stringify(v));
          }
        } catch {
          /* ignore */
        }
      } else {
        setError(formatApiError(err, 'Could not save currency'));
      }
    } finally {
      setCurrencyBusy(false);
    }
  };

  const saveDeliveryFee = async () => {
    setError('');
    setSuccess('');
    setFeeBusy(true);
    const payload = {
      display_currency: displayCurrency,
      delivery_fee_amount: deliveryFee,
    };
    try {
      await api.patch('/vendor/auth/local-partner-settings', payload);
      await refreshShop();
      setSuccess(
        `Delivery fee set to ${formatFeeOption(deliveryFee, displayCurrency)}. GoSnel commission applies to products + delivery.`,
      );
    } catch (err) {
      setError(formatApiError(err, 'Could not save delivery fee'));
    } finally {
      setFeeBusy(false);
    }
  };

  const saveMinimumOrder = async () => {
    setError('');
    setSuccess('');
    const raw = String(minimumOrder || '').trim();
    const value = raw === '' ? 0 : Number(raw);
    if (!Number.isFinite(value) || value < 0) {
      setError('Enter a valid minimum order amount (0 = no minimum).');
      return;
    }
    setMinOrderBusy(true);
    try {
      await api.patch('/vendor/auth/local-partner-settings', {
        display_currency: displayCurrency,
        minimum_order_amount: value,
      });
      await refreshShop();
      setSuccess(
        value > 0
          ? `Minimum order set to ${displayCurrency} ${value.toFixed(2)}. Residents see this on your shop.`
          : 'Minimum order cleared — no minimum for residents.',
      );
    } catch (err) {
      setError(formatApiError(err, 'Could not save minimum order'));
    } finally {
      setMinOrderBusy(false);
    }
  };

  const saveOrderLang = async () => {
    setError('');
    setSuccess('');
    setOrderLangBusy(true);
    try {
      await api.put('/vendor/auth/settings', { neighbour_order_lang: orderLang });
      await refreshShop();
      setSuccess(`Order language set to ${orderLangLabel(orderLang)}.`);
    } catch (err) {
      setError(formatApiError(err, 'Could not save order language'));
    } finally {
      setOrderLangBusy(false);
    }
  };

  const downloadPng = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `${(vendor?.name || 'gosnel-shop').replace(/\s+/g, '-').toLowerCase()}-walk-in-qr.png`;
    a.click();
  };

  const downloadA5Pdf = async () => {
    if (!qrDataUrl) return;
    setPdfBusy(true);
    setError('');
    try {
      const shopName = vendor?.name || 'Your shop';
      const radius = vendor?.delivery_radius_km ?? 0.5;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
      const pageW = doc.internal.pageSize.getWidth();
      const cx = pageW / 2;

      doc.setTextColor(15, 118, 110);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('GOSNEL NEIGHBOUR', cx, 22, { align: 'center' });

      doc.setFontSize(18);
      doc.text(shopName, cx, 34, { align: 'center', maxWidth: pageW - 24 });

      doc.setTextColor(51, 65, 85);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text('Scan to order from your building', cx, 44, { align: 'center' });

      const qrSize = 85;
      const qrX = (pageW - qrSize) / 2;
      doc.addImage(qrDataUrl, 'PNG', qrX, 50, qrSize, qrSize);

      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      const urlLines = doc.splitTextToSize(walkInUrl, pageW - 28);
      doc.text(urlLines, cx, 142, { align: 'center' });

      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Acrylic stand instructions', cx, 158, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const instructions = [
        '1. Print this card on A5 (148 × 210 mm).',
        '2. Place it in an acrylic stand near your entrance.',
        `3. Walk-in residents scan → register on GoSnel Neighbour within your ${radius} km delivery radius.`,
      ];
      let y = 166;
      instructions.forEach((line) => {
        const wrapped = doc.splitTextToSize(line, pageW - 28);
        doc.text(wrapped, 14, y);
        y += wrapped.length * 5 + 2;
      });

      const fileBase = (shopName || 'gosnel-shop').replace(/\s+/g, '-').toLowerCase();
      doc.save(`${fileBase}-walk-in-qr-a5.pdf`);
    } catch (err) {
      setError(err?.message || 'Could not generate A5 PDF. Try Download PNG instead.');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={900} sx={{ mb: 0.5 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Account, currency, order language, walk-in QR, and support.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2, bgcolor: '#f0fdfa' }}>
        <Typography fontWeight={800} sx={{ mb: 0.5 }}>
          App update (phone Home Screen)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          If ads or screens look old on your phone after we ship a fix, tap Update app. This clears
          the saved copy and reloads — works when Safari hard refresh is not available.
        </Typography>
        <Button
          variant="contained"
          startIcon={<SystemUpdateAltIcon />}
          onClick={() => forceHardRefresh()}
          sx={{ fontWeight: 800 }}
        >
          Update app now
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Account
        </Typography>
        <Stack spacing={0.75}>
          <Typography variant="body2">
            <strong>Shop:</strong> {vendor?.name || '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Type:</strong> {typeLabel(vendor?.vendor_type)}
          </Typography>
          <Typography variant="body2">
            <strong>Login email:</strong> {vendor?.email || '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Portal:</strong> {PORTAL_URL}
          </Typography>
        </Stack>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={async () => {
            setError('');
            try {
              await refreshShop();
              setSuccess('Profile refreshed from server.');
            } catch (err) {
              setError(formatApiError(err, 'Refresh failed'));
            }
          }}
        >
          Refresh shop data
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Currency
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Display currency for prices in this portal (and Neighbour when supported).
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="currency-label">Display currency</InputLabel>
            <Select
              labelId="currency-label"
              label="Display currency"
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency(e.target.value)}
            >
              {DISPLAY_CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={saveCurrency} disabled={currencyBusy}>
            {currencyBusy ? <CircularProgress size={18} color="inherit" /> : 'Save currency'}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Neighbour delivery fee
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Flat fee added to each Neighbour order total. GoSnel commission is calculated on
          products + delivery (example: 9 + 1 = 10 → 10% = 1.00 from your wallet).
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="delivery-fee-label">Delivery fee</InputLabel>
            <Select
              labelId="delivery-fee-label"
              label="Delivery fee"
              value={deliveryFee}
              onChange={(e) => setDeliveryFee(Number(e.target.value))}
            >
              {feeOptions.map((opt) => (
                <MenuItem key={String(opt)} value={opt}>
                  {formatFeeOption(opt, displayCurrency)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={saveDeliveryFee} disabled={feeBusy}>
            {feeBusy ? <CircularProgress size={18} color="inherit" /> : 'Save delivery fee'}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Minimum order
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Residents see this on your shop profile when they browse by shop. Leave empty or 0 for no
          minimum. Amount is in your display currency ({displayCurrency}).
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label={`Minimum order (${displayCurrency})`}
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            value={minimumOrder}
            onChange={(e) => setMinimumOrder(e.target.value)}
            placeholder="0"
            sx={{ minWidth: 220 }}
          />
          <Button variant="contained" onClick={saveMinimumOrder} disabled={minOrderBusy}>
            {minOrderBusy ? <CircularProgress size={18} color="inherit" /> : 'Save minimum order'}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Order language
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose the language your team prefers for resident orders on GoSnel Neighbour.
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
          {ORDER_LANG_OPTIONS.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              color={orderLang === opt.value ? 'primary' : 'default'}
              variant={orderLang === opt.value ? 'filled' : 'outlined'}
              onClick={() => setOrderLang(opt.value)}
              sx={{ fontWeight: 800 }}
            />
          ))}
        </Stack>
        <Button variant="contained" onClick={saveOrderLang} disabled={orderLangBusy}>
          {orderLangBusy ? <CircularProgress size={18} color="inherit" /> : 'Save order language'}
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 0.5 }}>
          Walk-in QR (A5)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Print and place near your entrance so building residents can order on GoSnel Neighbour.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'stretch' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#f0fdfa',
              p: 2.5,
              textAlign: 'center',
              maxWidth: 360,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={900}
              sx={{ color: '#0f766e', letterSpacing: 0.06, textTransform: 'uppercase' }}
            >
              GoSnel Neighbour
            </Typography>
            <Typography fontWeight={900} sx={{ color: '#0f766e', mt: 0.5, mb: 0.5 }}>
              {vendor?.name || 'Your shop'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Scan to order from your building
            </Typography>
            {qrDataUrl ? (
              <Box
                component="img"
                src={qrDataUrl}
                alt="Walk-in QR"
                sx={{ width: '100%', maxWidth: 220, height: 'auto', mx: 'auto' }}
              />
            ) : (
              <CircularProgress size={28} sx={{ my: 4 }} />
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, wordBreak: 'break-all' }}>
              {walkInUrl}
            </Typography>
          </Box>

          <Stack spacing={1.5} sx={{ flex: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <strong>How to use</strong>
              <Box component="ol" sx={{ m: 0, pl: 2.25, mt: 0.75 }}>
                <li>Print this card on A5 (148×210 mm).</li>
                <li>Put it in an acrylic stand near your entrance.</li>
                <li>
                  Walk-in residents scan → register on GoSnel Neighbour within your delivery radius (
                  {vendor?.delivery_radius_km ?? 0.5} km).
                </li>
              </Box>
            </Alert>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={downloadPng}
                disabled={!qrDataUrl}
              >
                Download PNG
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  pdfBusy ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />
                }
                onClick={downloadA5Pdf}
                disabled={!qrDataUrl || pdfBusy}
              >
                Download A5 PDF
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Support
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Need help with Neighbour orders, wallet, or onboarding? Chat with Partner Support or ask
          GoSnel AI.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Chat with GoSnel Partner Support
          </Button>
          <Button
            component={RouterLink}
            to="/ai"
            variant="outlined"
            startIcon={<SmartToyIcon />}
          >
            Open GoSnel AI
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Change password
        </Typography>
        <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
          <TextField
            type="password"
            label="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
          />
          <TextField
            type="password"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
          />
          <TextField
            type="password"
            label="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={changePassword} disabled={busy}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Update password'}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Session
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Sign out on shared devices after you finish.
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Button color="error" variant="outlined" onClick={logout}>
          Log out
        </Button>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 2, borderColor: 'error.light', bgcolor: 'rgba(211,47,47,0.02)' }}
      >
        <Typography fontWeight={800} sx={{ mb: 1 }} color="error.main">
          Delete shop account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          Local Partner accounts cannot be self-deleted online (orders, wallet, and ads history).
          Message Partner Support on WhatsApp and we will close the shop safely.
        </Typography>
        <Button
          color="error"
          variant="contained"
          startIcon={<WhatsAppIcon />}
          href={`${SUPPORT_WHATSAPP_URL}?text=${encodeURIComponent(
            `Hi GoSnel — please delete / close my Local Partner shop account.\nShop: ${vendor?.name || ''}\nEmail: ${vendor?.email || ''}`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact WhatsApp to delete account
        </Button>
      </Paper>
    </Box>
  );
}
